import { Archive, ArchiveRestore, ClipboardList, Plus, Trash2 } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import NoSelectionPrompt from "../components/NoSelectionPrompt.tsx";
import { api, type ProspectList } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import AgentActivity from "../components/AgentActivity.tsx";
import CollaboratorStack from "../components/CollaboratorStack.tsx";
import EmptyState from "../components/EmptyState.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import IdentityCell from "../components/IdentityCell.tsx";
import LiveUpdatesPill from "../components/LiveUpdatesPill.tsx";
import ListSelector from "../components/ListSelector.tsx";
import CenteredSpinner from "../components/CenteredSpinner.tsx";
import RelativeTime from "../components/RelativeTime.tsx";
import RowActions from "../components/RowActions.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import { useFilters, useLocalFilterState } from "../components/filters/useFilters.ts";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import TableCard from "../components/table/TableCard.tsx";
import { collectionPath, useTenant } from "../contexts/TenantContext.tsx";
import type { FilterSpec } from "../components/filters/specs.ts";
import { usePointerInside, useLiveList } from "../realtime/useLiveList.ts";
import { formatNumber } from "../utils/format.ts";
import SignalListChip from "./prospect-list-detail/components/SignalListChip.tsx";
import CollectionOrigin from "../components/prospects/CollectionOrigin.tsx";
import PageHeader from "../components/PageHeader.tsx";

// Search only: a tenant's collections are few enough to scan.
const NO_FILTERS: FilterSpec[] = [];
const listKey = (l: ProspectList) => l.id;

function ProspectListsPage() {
  const { selectedTenantId, selectedTenant, tenantPath } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const filterState = useLocalFilterState();
  const filters = useFilters(NO_FILTERS, {
    state: filterState,
    search: { param: "q", placeholder: "Search name or description" },
  });
  const search = filterState.searchParams.get("q") ?? "";

  const lists = useQuery({
    queryKey: keys.prospectList.list(selectedTenantId ?? ""),
    queryFn: () => api.getProspectLists(selectedTenantId!),
    enabled: !!selectedTenantId,
    meta: { live: true },
  });
  // Pushed live; held still while the mouse is on the table, about to click a row.
  const pointer = usePointerInside();
  const live = useLiveList({
    rows: lists.data,
    getId: listKey,
    frozen: pointer.inside,
    resetKey: selectedTenantId ?? "",
  });

  // The same calls the list's own page makes from its header.
  const archiveList = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.updateProspectList(id, { archived }),
    onSuccess: (_data, { archived }) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      toast.success(archived ? "Collection archived" : "Collection restored");
    },
  });

  const deleteList = useMutation({
    mutationFn: (id: string) => api.deleteProspectList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      toast.success("Collection deleted");
    },
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = live.rows ?? [];
    if (!q) return all;
    return all.filter(
      (l) => l.name.toLowerCase().includes(q) || (l.description ?? "").toLowerCase().includes(q),
    );
  }, [live.rows, search]);

  if (!selectedTenantId) {
    return (
      <>
        <PageHeader title="Collections" />
        <NoSelectionPrompt kind="tenant" />
      </>
    );
  }

  const createButton = (
    <Button variant="contained" startIcon={<Plus />} onClick={() => setShowCreate(true)}>
      Create collection
    </Button>
  );
  const count = lists.data?.length;
  const listCount = (n: number) => `${formatNumber(n)} ${n === 1 ? "collection" : "collections"}`;

  return (
    <>
      <PageHeader
        title="Collections"
        description={
          <>
            Named sets of companies and people{selectedTenant ? ` for ${selectedTenant.name}` : ""}
          </>
        }
        actions={createButton}
      />
      <Typography variant="body2" component="div" sx={{ color: "text.secondary", mb: 2, maxWidth: 760 }}>
        You make some collections yourself; agents fill others.
        <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
          <li>
            <strong>Signal workflows</strong>: each signal workflow keeps one collection (marked Signal
            collection). Its research agent adds every buyer it looks into, with its verdict, and contact
            research adds the people it finds there. Decide on those companies in the{" "}
            <Link component={RouterLink} to={tenantPath("/prospects/inbox")} underline="hover">
              Prospects inbox
            </Link>
            .
          </li>
          <li>
            <strong>Prospecting agent runs</strong>: the agent working a prospect order puts the companies and
            contacts it finds in the order's collection.
          </li>
        </Box>
        An Add to collection step chained under an agent copies that run's finds into another collection, e.g.
        one you export. The Source column shows what made each collection.
      </Typography>
      {lists.isLoading && <CenteredSpinner />}
      {lists.data && lists.data.length === 0 && (
        <EmptyState
          variant="panel"
          icon={<ClipboardList />}
          title="No collections yet"
          description="Create one to start organizing prospects."
          action={createButton}
        />
      )}
      {lists.data && lists.data.length > 0 && (
        <Box {...pointer.handlers}>
          <TableCard
            notice={<LiveUpdatesPill pendingNew={live.pendingNew} pendingRemoved={live.pendingRemoved} onApply={live.apply} />}
            toolbar={
              <FilterBar
                filters={filters}
                count={
                  count == null
                    ? undefined
                    : filters.activeCount > 0
                      ? `${formatNumber(rows.length)} of ${formatNumber(count)}`
                      : listCount(count)
                }
              />
            }
          >
            <ScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell align="right">Prospects</TableCell>
                  <TableCell align="right">Signals</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((list) => (
                  <TableRow
                    key={list.id}
                    hover
                    aria-disabled={live.removedIds.has(list.id) || undefined}
                    sx={{ cursor: "pointer", ...(live.removedIds.has(list.id) && { opacity: 0.5 }) }}
                    onClick={() => navigate(tenantPath(collectionPath(list.id)))}
                  >
                    <TableCell sx={{ maxWidth: 480 }}>
                      <IdentityCell
                        name={list.name}
                        tag={
                          (list.collection_type === "signal" ||
                            list.archived ||
                            list.active_agent_runs.length > 0) && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                              <AgentActivity runs={list.active_agent_runs} />
                              {list.collection_type === "signal" && <SignalListChip />}
                              {list.archived && <Chip label="Archived" size="small" variant="outlined" />}
                            </Box>
                          )
                        }
                        caption={list.description || undefined}
                      />
                    </TableCell>
                    <TableCell>
                      <CollectionOrigin origin={list.origin} />
                    </TableCell>
                    <TableCell>
                      <CollaboratorStack
                        creator={
                          list.created_by_user_id
                            ? {
                                user_id: list.created_by_user_id,
                                display_name: list.created_by_display_name,
                                email: list.created_by_email,
                                picture_url: list.created_by_picture_url,
                              }
                            : null
                        }
                        collaborators={list.collaborators}
                        maxVisible={3}
                        size={28}
                      />
                    </TableCell>
                    <TableCell align="right">{formatNumber(list.prospect_count)}</TableCell>
                    <TableCell align="right">{formatNumber(list.signal_trigger_count)}</TableCell>
                    <TableCell>
                      <FormattedDate value={list.created_at} />
                    </TableCell>
                    <TableCell>
                      <RelativeTime value={list.updated_at} />
                    </TableCell>
                    <TableCell padding="checkbox" align="right">
                      <RowActions
                        label={list.name}
                        actions={[
                          {
                            label: list.archived ? "Restore" : "Archive",
                            icon: list.archived ? (
                              <ArchiveRestore fontSize="small" />
                            ) : (
                              <Archive fontSize="small" />
                            ),
                            onClick: () => archiveList.mutate({ id: list.id, archived: !list.archived }),
                          },
                          {
                            label: "Delete",
                            icon: <Trash2 fontSize="small" />,
                            destructive: true,
                            onClick: () => deleteList.mutateAsync(list.id).then(() => live.drop(list.id)),
                            confirm: {
                              title: "Delete collection",
                              body: `Delete "${list.name}" and all its prospect entries? This cannot be undone.`,
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <EmptyTableRow colSpan={8}>No collections match your search</EmptyTableRow>
                )}
              </TableBody>
            </ScrollTable>
          </TableCard>
        </Box>
      )}
      <ListSelector
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSelect={(listId) => {
          setShowCreate(false);
          navigate(tenantPath(collectionPath(listId)));
        }}
      />
    </>
  );
}

export default ProspectListsPage;
